import { t } from '../i18n';
import { useState, useEffect } from 'react';
import { Mail, User, Send, MapPin, Linkedin, Twitter, ArrowUpRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const toastId = toast.loading(t("Sending message..."));

    try {
      await api.post('/contact', formData);
      toast.success(t("Message sent successfully!"), { id: toastId });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Failed to send contact message:', error);
      toast.error(t("Failed to send message. Please try again."), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, title: 'Email', content: 'contact@virtuoz-solutions.com', link: 'mailto:contact@virtuoz-solutions.com' },
    { icon: MapPin, title: t("Location"), content: 'Angers, France', link: null },
  ];

  const faqs = [
    { q: t("What types of projects do you work on?"), a: t("I specialize in web development, AI integration, and automation projects.") },
    { q: t("What is your typical process?"), a: t("Initial consultation, requirements gathering, proposal, development, testing, and deployment.") },
    { q: t("How do you handle pricing?"), a: t("I offer both hourly rates and fixed-price quotes depending on the project.") },
    { q: t("Do you provide ongoing maintenance?"), a: t("Yes, through retainer agreements or as-needed arrangements.") },
  ];

  return (
    <div className="content-panel">
      {/* Header */}
      <section
        className={`py-8 md:py-12 transition-all duration-700 ease-out-expo ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        {/* Decorative line */}
        <div className="w-12 h-px bg-gradient-to-r from-primary to-transparent mb-8" />

        <h1 className="font-display text-fluid-3xl mb-6">{t("Get in Touch")}</h1>
        <p className="text-lg text-muted max-w-2xl leading-relaxed">
          {t("Have a project in mind or want to discuss a potential collaboration?")}{' '}
          <span className="text-foreground">{t("I'd love to hear from you!")}</span>
        </p>
      </section>

      {/* Contact Form and Info */}
      <section className="py-8">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] 2xl:gap-12">
          {/* Contact Form */}
          <div
            className={`card p-6 relative transition-all duration-700 ease-out-expo ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            {/* Corner accent */}
            <div
              className="absolute top-0 right-0"
              style={{
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 50px 50px 0',
                borderColor: 'transparent rgb(var(--color-primary) / 0.15) transparent transparent',
              }}
            />

            <h2 className="text-xl font-heading font-bold mb-6">{t("Send a Message")}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative">
                <label
                  htmlFor="name"
                  className={`label transition-all duration-300 ${
                    focusedField === 'name' || formData.name ? 'text-primary' : ''
                  }`}
                >
                  {t("Your Name")}
                </label>
                <div className="relative">
                  <User className={`absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${
                    focusedField === 'name' ? 'text-primary' : 'text-muted'
                  }`} />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="input pl-6"
                    placeholder={t("John Doe")}
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    required
                  />
                </div>
              </div>

              <div className="relative">
                <label
                  htmlFor="email"
                  className={`label transition-all duration-300 ${
                    focusedField === 'email' || formData.email ? 'text-primary' : ''
                  }`}
                >
                  {t("Your Email")}
                </label>
                <div className="relative">
                  <Mail className={`absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${
                    focusedField === 'email' ? 'text-primary' : 'text-muted'
                  }`} />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="input pl-6"
                    placeholder="john.doe@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                  />
                </div>
              </div>

              <div className="relative">
                <label
                  htmlFor="subject"
                  className={`label transition-all duration-300 ${
                    focusedField === 'subject' || formData.subject ? 'text-primary' : ''
                  }`}
                >
                  {t("Subject")}
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="input"
                  placeholder={t("How can I help you?")}
                  value={formData.subject}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('subject')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
              </div>

              <div className="relative">
                <label
                  htmlFor="message"
                  className={`label transition-all duration-300 ${
                    focusedField === 'message' || formData.message ? 'text-primary' : ''
                  }`}
                >
                  {t("Your Message")}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  className="input resize-none"
                  placeholder={t("Tell me about your project...")}
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
              </div>

              <button
                type="submit"
                className={`btn btn-primary w-full ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t("Sending...")}
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t("Send Message")}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div
            className={`space-y-8 transition-all duration-700 ease-out-expo ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <div>
              <h2 className="section-title">
                <span>{t("Contact Information")}</span>
              </h2>
              <div className="space-y-5">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 group">
                      <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 group-hover:shadow-glow transition-all duration-300">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground">{item.title}</h3>
                        {item.link ? (
                          <a href={item.link} className="text-muted hover:text-primary transition-colors duration-300 text-sm">
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-muted text-sm">{item.content}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-6 border-t border-foreground/5">
              <h3 className="font-heading font-semibold mb-4">{t("Connect on Social Media")}</h3>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.linkedin.com/in/enzo-chevallier-6a69b77b/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 px-4 py-3 bg-card border border-foreground/5 rounded-lg text-muted hover:text-primary hover:border-primary/30 hover:shadow-glow transition-all duration-300"
                >
                  <Linkedin className="w-5 h-5" />
                  <span className="font-heading">LinkedIn</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href="https://x.com/EnzoChevallier"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 px-4 py-3 bg-card border border-foreground/5 rounded-lg text-muted hover:text-primary hover:border-primary/30 hover:shadow-glow transition-all duration-300"
                >
                  <Twitter className="w-5 h-5" />
                  <span className="font-heading">X</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>

            <div className="pt-6">
              <h3 className="font-heading font-semibold mb-3">{t("Availability")}</h3>
              <p className="text-muted text-sm leading-relaxed">
                {t("I'm currently")} <span className="text-primary">{t("available")}</span> {t("for freelance work and consulting. Typical response time is within 24-48 hours.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        className={`py-8 md:py-12 transition-all duration-700 ease-out-expo ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
        style={{ transitionDelay: '300ms' }}
      >
        <h2 className="section-title">
          <span>{t("Frequently Asked Questions")}</span>
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="group card card-hover p-5 relative"
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: 'radial-gradient(circle at center, rgb(var(--color-primary) / 0.03) 0%, transparent 70%)',
                }}
              />
              <div className="relative">
                <h3 className="font-heading font-bold mb-2 text-foreground">{faq.q}</h3>
                <p className="text-muted text-sm leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Contact;
